// Havi manuális árfolyam-panel (a UsageDataChecker szűrősávjából) — a legacy „árfolyam megadása a generátor
// ELŐTT" 1:1, de MŰKÖDŐ szerkesztéssel és törléssel.
//
// MI VOLT A BAJ (user): „az árfolyam gombra kattintva nem tudok árfolyamot rögzíteni, illetve régit
// módosítani, vagy épp törölni." Három külön ok, mind javítva:
//   1. A bevitel CSAK PONTOT fogadott el (`/^\d+(\.\d+)?$/`), a magyar tizedes VESSZŐ csendben letiltotta a
//      Mentés gombot — magyarázat nélkül. Most a vessző is jó (mentés előtt pontra normalizálunk), és
//      érvénytelen bevitelre KIÍRJUK, mi a szabály.
//   2. A meglévő kurzus SORA csak kijelzés volt; a módosításhoz a lenti űrlapon újra ki kellett választani a
//      devizát. Most minden sor SAJÁT szerkesztő-gombot kap (a meglévő érték előtöltve) — a külön „felvitel"
//      blokk megszűnt.
//   3. TÖRLÉS EGYÁLTALÁN NEM LÉTEZETT (az Odin API-jából is hiányzott). Most van:
//      `DELETE /currency-rates/:base/:quote/:date` → Odin `DELETE /v1/currency-rates/...` (hard delete, mert a
//      `currency_rates` időbeli referencia-idősor: egy „törölt, de meglévő" sor a step-function feloldást
//      csendben elrontaná). Idempotens: nem létező sorra is 200.
//
// JOG: a módosítás kapuja a SZÁMLÁZÁS-kezelés (`invoices.manage`) — az árfolyam a számla-generátor bemenete.
// Az Odin-fal ugyanez (billing-írás vagy platform:admin). A read bárkinek megy.
import { BanknotesIcon, CheckIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type TFunction } from 'i18next';
import { useState } from 'react';
import { toast } from 'sonner';

import { usageKeys } from '../api';

import { Button } from '@/components/ui/button';
import { Divider } from '@/components/ui/divider';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Text } from '@/components/ui/text';
import { api } from '@/lib/api';

type Base = 'EUR' | 'USD';
const BASES: Base[] = ['EUR', 'USD'];

/** A szerver-oldali (contract) alak: PONT a tizedes-jel, max 8 tizedes. */
const RATE_RE = /^\d{1,10}(\.\d{1,8})?$/;

/**
 * A beírt érték normalizálása a szerződés alakjára: tizedes VESSZŐ → pont, körbevágás, ezres-szóközök ki.
 * A magyar numpad vesszőt ad, és a felhasználó is vesszőt ír — ezt a felület dolga lefordítani, nem a
 * felhasználóé (korábban a vesszős bevitel csak letiltotta a mentést, magyarázat nélkül).
 */
function normalizeRate(raw: string): string {
  return raw.trim().replace(/\s/g, '').replace(',', '.');
}

/**
 * A havi manuális árfolyam-panel. A `month` (`YYYY-MM`) a hó 1-jét címzi (`rateDate`); a `canManage`
 * (= `invoices.manage`) kapuzza a szerkesztést/törlést. A read bárkinek — a lista a hó 1-jére + `manual`-ra szűr.
 */
export function CurrencyRatePanel({
  month,
  canManage,
  open,
  onOpenChange,
  t,
}: {
  month: string;
  canManage: boolean;
  /**
   * KÍVÜLRŐL VEZÉRELT nyitás — a MOBIL FAB innen nyitja a panelt (ott a sáv-gomb rejtve van). Elhagyva a
   * panel a saját trigger-gombjával működik, ahogy eddig.
   */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  t: TFunction;
}): JSX.Element {
  const qc = useQueryClient();
  const firstOfMonth = `${month}-01`;
  /** Melyik deviza sora van SZERKESZTÉS alatt (egyszerre egy) + a piszkozat-érték. */
  const [editing, setEditing] = useState<Base | null>(null);
  const [draft, setDraft] = useState('');

  const ratesQuery = api.currencyRatesList.useQuery(usageKeys.rates(), {
    query: { quote: 'HUF' },
  });
  const allRates = ratesQuery.data?.status === 200 ? ratesQuery.data.body.rates : [];
  // A kiválasztott hó 1-jére érvényes manuális árfolyamok (EUR/USD → HUF).
  const monthlyManual = allRates.filter(
    (r) => r.source === 'manual' && r.rateDate === firstOfMonth && r.quoteCurrency === 'HUF',
  );
  const byBase = new Map(monthlyManual.map((r) => [r.baseCurrency, r.rate]));

  const closeEditor = (): void => {
    setEditing(null);
    setDraft('');
  };

  const upsert = useMutation({
    mutationFn: (v: { base: Base; rate: string }) =>
      api.currencyRateUpsert.mutation({
        body: {
          baseCurrency: v.base,
          quoteCurrency: 'HUF',
          rateDate: firstOfMonth,
          rate: v.rate,
        },
      }),
    onSuccess: (res) => {
      if (res.status === 200) {
        toast.success(t('toast:usageData.rateSaved'));
        closeEditor();
        void qc.invalidateQueries({ queryKey: usageKeys.rates() });
      } else if (res.status === 403) {
        toast.error(t('toast:usageData.rateForbidden'));
      } else if (res.status === 422) {
        toast.error(t('toast:usageData.rateValidation'));
      } else {
        toast.error(t('toast:usageData.rateError'));
      }
    },
    onError: () => toast.error(t('toast:usageData.rateError')),
  });

  const remove = useMutation({
    mutationFn: (base: Base) =>
      // A DELETE-nek NINCS törzse (a ts-rest kliens ezért `body`-t sem vár) — a természetes kulcs az útvonalon.
      api.currencyRateDelete.mutation({ params: { base, quote: 'HUF', date: firstOfMonth } }),
    onSuccess: (res) => {
      if (res.status === 200) {
        toast.success(t('toast:usageData.rateDeleted'));
        closeEditor();
        void qc.invalidateQueries({ queryKey: usageKeys.rates() });
      } else if (res.status === 403) {
        toast.error(t('toast:usageData.rateForbidden'));
      } else {
        toast.error(t('toast:usageData.rateDeleteError'));
      }
    },
    onError: () => toast.error(t('toast:usageData.rateDeleteError')),
  });

  // A trigger-felirat: a hó két manuális árfolyamának rövid összefoglalója (vagy „nincs").
  const summary = BASES.map((b) => (byBase.has(b) ? `${b} ${byBase.get(b)!}` : null))
    .filter((x): x is string => x !== null)
    .join(' · ');

  const normalized = normalizeRate(draft);
  const draftValid = RATE_RE.test(normalized);
  const pending = upsert.isPending || remove.isPending;

  return (
    <Popover
      // KÍVÜLRŐL VEZÉRELT nyitás, ha a hívó megadja (a mobil FAB innen nyitja); egyébként a Popover a saját
      // állapotát viszi, ahogy eddig. A záráskor a SOR-SZERKESZTŐ is becsukódik — nyitva felejtve a
      // következő nyitásnál egy idegen deviza piszkozata bukkanna elő.
      {...(open === undefined ? {} : { open })}
      onOpenChange={(next) => {
        if (!next) closeEditor();
        onOpenChange?.(next);
      }}
    >
      <PopoverTrigger asChild>
        <Button variant="outline" className="shrink-0" aria-label={t('usageData.rates.title')}>
          <BanknotesIcon className="h-4 w-4" />
          <span className="hidden sm:inline">{summary || t('usageData.rates.none')}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 space-y-3">
        <div>
          <Text variant="itemTitle" as="div">
            {t('usageData.rates.title')}
          </Text>
          <Text variant="secondary" as="div" className="text-xs">
            {t('usageData.rates.subtitle')}
          </Text>
        </div>

        {/* DEVIZÁNKÉNT EGY SOR: érték + (jog esetén) szerkesztés/törlés. Szerkesztés alatt a sor beviteli
            mezőre vált, a meglévő értékkel előtöltve — így a „régi módosítása" ott van, ahol a felhasználó
            keresi (a soron), nem egy külön űrlapon. */}
        <div className="space-y-1.5">
          {BASES.map((b) => {
            const current = byBase.get(b) ?? null;
            const isEditing = editing === b;
            return (
              <div
                key={b}
                className="border-border flex min-h-9 items-center gap-2 rounded-lg border px-3 py-1.5"
              >
                <Text variant="body" as="span" className="shrink-0">
                  {b} → HUF
                </Text>
                {isEditing ? (
                  <>
                    <Input
                      autoFocus
                      value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && draftValid && !pending) {
                          upsert.mutate({ base: b, rate: normalized });
                        }
                        if (e.key === 'Escape') closeEditor();
                      }}
                      placeholder={t('usageData.rates.ratePlaceholder')}
                      inputMode="decimal"
                      aria-label={t('usageData.rates.rate')}
                      className="ml-auto h-7 w-28 text-right"
                    />
                    <Button
                      type="button"
                      variant="brand"
                      size="icon"
                      className="size-7 shrink-0"
                      aria-label={t('usageData.rates.save')}
                      disabled={!draftValid || pending}
                      onClick={() => upsert.mutate({ base: b, rate: normalized })}
                    >
                      <CheckIcon className="size-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-7 shrink-0 px-2"
                      onClick={closeEditor}
                      disabled={pending}
                    >
                      {t('usageData.rates.cancel')}
                    </Button>
                  </>
                ) : (
                  <>
                    <Text variant="body" as="span" className="ml-auto font-medium tabular-nums">
                      {current ?? (
                        <span className="text-muted-foreground font-normal">
                          {t('usageData.rates.missing')}
                        </span>
                      )}
                    </Text>
                    {canManage && (
                      <>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="size-7 shrink-0"
                          aria-label={t(
                            current === null
                              ? 'usageData.rates.addRate'
                              : 'usageData.rates.editRate',
                            { base: b },
                          )}
                          disabled={pending}
                          onClick={() => {
                            setEditing(b);
                            setDraft(current ?? '');
                          }}
                        >
                          <PencilSquareIcon className="size-4" />
                        </Button>
                        {/* Törlés CSAK létező soron — nincs mit törölni, ha nincs érték. */}
                        {current !== null && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="text-danger hover:text-danger size-7 shrink-0"
                            aria-label={t('usageData.rates.deleteRate', { base: b })}
                            disabled={pending}
                            onClick={() => remove.mutate(b)}
                          >
                            <TrashIcon className="size-4" />
                          </Button>
                        )}
                      </>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>

        {/* Miért NEM lehet mentni (a néma tiltott gomb helyett), illetve mit tesz a mentés. */}
        {canManage ? (
          <Text variant="secondary" as="p" className="text-xs">
            {editing !== null && draft.trim() !== '' && !draftValid
              ? t('usageData.rates.invalidHint')
              : t('usageData.rates.hint')}
          </Text>
        ) : (
          <>
            <Divider spacing="sm" />
            <Text variant="secondary" as="p" className="text-xs">
              {t('usageData.rates.readOnly')}
            </Text>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}
